/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Button, Grid, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch } from 'react-redux';
import Theme from '../ui/Theme';
import PopupText from '../common/PopupText';
import PopupTextField from '../common/PopupTextField';
import { getCustomGroupDetails, setCustomGroupsListData, showProgress } from '../../redux/actions/DealershipActions';
import MultiSelectSearch from '../common/search/MultiSelectSearch';
import service from '../../apis/service';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import { apiEndpoints } from '../../constants/apiEndpoints';

const customStyles = {
  content: {
    height: '400px',
    width: '511px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '15px',
  },
};
const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  popupHeading: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '22px',
    fontStyle: 'normal',
    marginBottom: '10px',
    marginTop: '0px',
  },
  popSubmitBtn: {
    padding: '0 70px',
    height: '45px',
    width: '200px',
  },
  popCancelBtn: {
    padding: '0 70px',
    height: '45px',
    width: '200px',
    marginRight: '28px',
  },
  popupCloseBtn: {
    borderRadius: '50%',
    backgroundColor: Theme.palette.secondary.main,
    padding: '8px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    cursor: 'pointer',
  },
  content: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}));

const CustomGroupsAddNewPopup = ({ close, add, selectedData, refreshData, handleListItemClick }) => {
  const classes = useStyles();
  const [data, setData] = useState({});
  const [selectedDealership, setSelectedDealership] = useState([]);
  const [dealershipList, setDealershipList] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!add) {
      setData({
        ...selectedData,
        dealerships: selectedData.dealerships
          ? selectedData.dealerships.map((d) => ({ label: d.name, value: d.id }))
          : [],
      });
      setSelectedDealership(
        selectedData.dealerships ? selectedData.dealerships.map((d) => ({ label: d.name, value: d.id })) : [],
      );
    }
  }, [add, selectedData]);

  useEffect(() => {
    const fetchDealershipList = async () => {
      dispatch(showProgress(true));
      try {
        const response = await service.post(apiEndpoints.GET_DEALERSHIPS_LIST, {
          filters: { sort: { key: 'createdAt', order: 'DESC' }, enabled: 'ENABLED' },
        });
        setDealershipList(response?.map((r) => ({ value: r.id, label: r.name })) || []);
        dispatch(showProgress(false));
      } catch (error) {
        ErrorToast(error);
        dispatch(showProgress(false));
      }
    };
    fetchDealershipList();
  }, [dispatch]);

  const handleDropdownChange = (value) => {
    setSelectedDealership(value);
    setData({ ...data, dealerships: value });
  };

  const handleDataChange = ({ target }) => {
    setData({ ...data, [target.name]: target.value });
  };

  const validateForm = () => {
    const errors = [];
    let formIsValid = true;
    if (!data.name) {
      formIsValid = false;
      errors.push('Please enter name of custom group.');
    }
    if (!data.dealerships || data.dealerships?.length === 0) {
      formIsValid = false;
      errors.push('Please add atleast one dealerships to custom group.');
    }
    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return formIsValid;
  };

  const fetchDataAfterUpdate = async () => {
    dispatch(showProgress(true));
    try {
      const response = await service.get(apiEndpoints.CG_LIST);
      dispatch(setCustomGroupsListData(response));
      dispatch(showProgress(false));
    } catch (error) {
      ErrorToast(error);
      dispatch(showProgress(false));
    }
  };

  const saveCustomGroup = async () => {
    const check = validateForm();
    const { name } = data;
    if (check) {
      dispatch(showProgress(true));
      const request = {
        name,
        dealerships: data.dealerships.map((d) => d.value).filter((d) => d !== '*'),
      };
      if (add) {
        try {
          const response = await service.post(apiEndpoints.CG_CREATE, request);
          handleListItemClick(response.id);
          refreshData();
          dispatch(showProgress(false));
          notifySuccess('Saved Successfully');
          close();
        } catch (error) {
          dispatch(showProgress(false));
          ErrorToast(error);
        }
      } else {
        try {
          await service.put(apiEndpoints.CG_UPDATE.replace('CG_ID', data.id), request);
          dispatch(showProgress(false));
          await fetchDataAfterUpdate();
          dispatch(getCustomGroupDetails(data.id));
          notifySuccess('Saved Successfully');
          close();
        } catch (error) {
          dispatch(showProgress(false));
          ErrorToast(error);
        }
      }
    }
  };

  return (
    <Modal isOpen onRequestClose={close} style={customStyles}>
      <Grid container direction="row" alignItems="center">
        <Grid item xs={11} className={classes.popupHeading}>
          {add ? 'Add Custom group' : 'Edit Custom Group'}
        </Grid>
        <Grid item xs={1}>
          <CloseIcon className={classes.popupCloseBtn} onClick={close} />
        </Grid>
      </Grid>
      <Grid container direction="column">
        <Grid item>
          <PopupText text="Custom Group Name" required />
        </Grid>
        <Grid item>
          <PopupTextField
            name="name"
            placeholder="Custom Group Name"
            id="name_id"
            height="44"
            handleChange={handleDataChange}
            value={data.name}
            width="100%"
          />
        </Grid>
        <Grid item>
          <PopupText text="Dealerships" required />
        </Grid>
        <Grid item style={{ marginTop: '15px' }}>
          <MultiSelectSearch
            placeholderButtonLabel="Select a Dealership"
            selectedOptions={selectedDealership}
            onChange={handleDropdownChange}
            options={dealershipList}
            name="dealerships"
            showValue
            width="100%"
            maxMenuHeight={120}
            labelWidth="400px"
          />
        </Grid>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" style={{ marginTop: '100px' }}>
        <Button variant="contained" className={classes.popCancelBtn} onClick={close}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" className={classes.popSubmitBtn} onClick={saveCustomGroup}>
          Save
        </Button>
      </Grid>
    </Modal>
  );
};

export default CustomGroupsAddNewPopup;
